/** Resumen legible de la configuración PROPIA de una categoría (paso Formato).
 *
 * Lección del t52 (Eduardo, 2026-07-30): la división 65 tenía
 * `seeding_source: "manual"` guardado en SU configuración. Al pedir "generar
 * por ranking" el cuadro salió 100% al azar, y cambiar el Seeding en otra
 * categoría (o en el formato general) no la afectaba — la config de división
 * SOBREESCRIBE al padre. Costó tres rearmes porque nada en pantalla decía ni
 * que la categoría tenía config propia ni qué decía esa config.
 *
 * Estas funciones son puras para poder testear el texto exacto que ve el
 * admin.
 */

export type DivisionFormatLike = {
  format_type?: string | null;
  format_config_json?: Record<string, unknown> | null;
};

/** Bloqueador 2 del demo FMT (2026-07-15): el backend serializa {} cuando la
 * categoría NO tiene formato propio, y {} es truthy — los chips marcaban ✓
 * categorías sin configurar (el patrón exacto del error Mex Tour Cup).
 * Formato propio = format_type + config con contenido, igual que el criterio
 * del readiness. */
export function divisionHasOwnFormat(division: DivisionFormatLike): boolean {
  return (
    Boolean(division.format_type)
    && Object.keys(division.format_config_json ?? {}).length > 0
  );
}

const FORMAT_LABELS: Record<string, string> = {
  elimination: "Eliminatoria",
  round_robin: "Round robin",
  ladder: "Escalera",
};

const SEEDING_LABELS: Record<string, string> = {
  initial_rank: "Ranking inicial",
  current_rank: "Ranking actual",
  manual: "Manual",
};

export interface DivisionFormatFact {
  label: string;
  value: string;
  /** true cuando el valor es una fuente de sorteo que NO usa el ranking: el
   * admin debe verlo antes de pedir "generar por ranking". */
  warn?: boolean;
}

/** Datos que el admin necesita ver SIN abrir el formulario. */
export function divisionFormatFacts(division: DivisionFormatLike): DivisionFormatFact[] {
  if (!divisionHasOwnFormat(division)) return [];
  const config = division.format_config_json ?? {};
  const facts: DivisionFormatFact[] = [];

  const formatType = division.format_type ?? "";
  facts.push({
    label: "Formato",
    value: FORMAT_LABELS[formatType] ?? formatType,
  });

  // Series por equipos (Mini Copa 2026): que se vea sin abrir el formulario.
  const teamPlay = config.team_play as { enabled?: boolean; team_size?: number; lines?: { kind?: string }[]; wins_to_take_tie?: number } | null | undefined;
  if (teamPlay && teamPlay.enabled) {
    const counts: Record<string, number> = {};
    for (const line of teamPlay.lines ?? []) {
      const kind = line?.kind ?? "singles";
      counts[kind] = (counts[kind] ?? 0) + 1;
    }
    const kindLabels: Record<string, string> = { singles: "singles", doubles: "dobles", mixed: "mixtos" };
    const parts = ["singles", "doubles", "mixed"].filter((kind) => counts[kind]).map((kind) => `${counts[kind]} ${kindLabels[kind]}`);
    facts.push({
      label: "Equipos",
      value: `${teamPlay.team_size ?? 2} jugadores · ${parts.join(" + ")} · gana con ${teamPlay.wins_to_take_tie ?? 2}`,
    });
  }

  const seeding = config.seeding_source;
  if (typeof seeding === "string") {
    facts.push({
      label: "Seeding",
      value: SEEDING_LABELS[seeding] ?? seeding,
      // "Manual" toma las posiciones del acomodo manual: si nadie las asignó,
      // un sorteo "por ranking" sale al azar sin avisar.
      warn: seeding === "manual",
    });
  }

  const bracketSize = config.bracket_size;
  if (typeof bracketSize === "number" && bracketSize > 0) {
    facts.push({ label: "Cuadro", value: `Ronda de ${bracketSize}` });
  }

  const roundCount = config.round_count;
  if (typeof roundCount === "number" && roundCount > 0) {
    facts.push({ label: "Jornadas", value: String(roundCount) });
  }

  return facts;
}

/** Aviso de precedencia: la config de la categoría gana sobre la general. */
export function divisionOverrideNotice(
  division: DivisionFormatLike,
  divisionName: string,
): string | null {
  if (!divisionHasOwnFormat(division)) return null;
  return (
    `${divisionName} tiene su PROPIA configuración: manda sobre el formato general `
    + "del torneo. Cambiar el formato en otra categoría no la modifica — edítala aquí."
  );
}
